import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../component-styles/Calendar.css';
import useProjects from '../../hooks/useProjects';
import TimeRangePicker from '../common/TimeRangePicker';
import { X, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { triggerPlannerSync } from '../utils/syncEvent';
import API_BASE from '../../utils/api';

/* ---------- Helpers ---------- */
function formatISO(date) {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function parseISO(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function addMonths(date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}
function formatAMPM(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = ((h + 11) % 12) + 1;
  return `${hour}:${m.toString().padStart(2, '0')} ${suffix}`;
}
function buildMonthMatrix(y, m) {
  const first = new Date(y, m, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const out = [];
  for (let w = 0; w < 6; w++) {
    const row = [];
    for (let d = 0; d < 7; d++) {
      row.push(new Date(start));
      start.setDate(start.getDate() + 1);
    }
    out.push(row);
  }
  return out;
}

const COLOR_PRESETS = ['#7fc59a', '#007673', '#eab308', '#ef4444', '#3b82f6', '#8b5cf6'];

export default function Calendar({
  initialProjectFilter,
  hideProjectSelector,
  fallbackProjects = [],
}) {
  const userId = localStorage.getItem('userId');
  const [cursor, setCursor] = useState(() => new Date());
  const [eventsMap, setEventsMap] = useState({});
  const [popoverDate, setPopoverDate] = useState(null);
  const [popoverPos, setPopoverPos] = useState({
    top: '50%',
    left: '50%',
  });
  const [expandedDay, setExpandedDay] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editDateIso, setEditDateIso] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [error, setError] = useState('');
  const [team, setTeam] = useState([]);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const assigneeRef = useRef(null);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  function selectMonthYear(m, y) {
    setCursor(new Date(y, m, 1));
    setMonthPickerOpen(false);
  }

  const [form, setForm] = useState({
    title: '',
    start: '09:00',
    end: '10:00',
    notes: '',
    project: '',
    color: '#7fc59a',
    allDay: false,
    assignees: [],
  });

  const { projects } = useProjects({ activeOnly: true });
  const projectColorMap = useMemo(() => {
    const map = {};
    (projects || []).forEach((p, i) => (map[p.id] = COLOR_PRESETS[i % COLOR_PRESETS.length]));
    return map;
  }, [projects]);

  const teamList = useMemo(() => {
    if (!assigneeSearch.trim()) return team;
    const q = assigneeSearch.toLowerCase();
    return team.filter(
      (m) =>
        (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    );
  }, [team, assigneeSearch]);

  const assigneeName = (email) =>
    team.find((m) => m.email === email)?.name || team.find((m) => m.email === email)?.email || email;

  /* ---------- Fetch Events ---------- */
  useEffect(() => {
    async function fetchEvents() {
      try {
        if (!userId) return;
        const res = await fetch(`${API_BASE}/api/planner/events/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`Bad response ${res.status}`);
        const data = await res.json();

        let filtered = data || {};
        if (initialProjectFilter) {
          const filteredMap = {};
          for (const [date, events] of Object.entries(filtered)) {
            const matching = events.filter((e) => e.projectId === initialProjectFilter);
            if (matching.length) filteredMap[date] = matching;
          }
          filtered = filteredMap;
        }
        const normalized = {};
        Object.entries(filtered).forEach(([date, events]) => {
          normalized[date] = (events || []).map((e) => ({
            ...e,
            assignees: Array.isArray(e.assignees)
              ? e.assignees.map((a) => (typeof a === 'string' ? a : a.email)).filter(Boolean)
              : [],
          }));
        });
        setEventsMap(normalized);
      } catch (err) {
        console.error('Failed to fetch planner events', err);
        setError('Failed to load events.');
      }
    }
    if (userId) fetchEvents();

    // 🔄 Listen for global sync updates
    function handleSync() {
      fetchEvents();
    }
    window.addEventListener('plannerSyncUpdated', handleSync);
    return () => window.removeEventListener('plannerSyncUpdated', handleSync);
  }, [userId, initialProjectFilter]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_BASE}/api/settings/${encodeURIComponent(userId)}`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => setTeam(Array.isArray(data.team) ? data.team : []))
      .catch(() => setTeam([]));
  }, [userId]);

  useEffect(() => {
    function handleOutside(e) {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target)) {
        setAssigneeOpen(false);
        setAssigneeSearch('');
      }
    }
    if (assigneeOpen) {
      document.addEventListener('mousedown', handleOutside);
    }
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [assigneeOpen]);

  /* ---------- Month Picker Click Outside ---------- */
  useEffect(() => {
    function handleClickOutside(e) {
      if (!monthPickerOpen) return;
      if (!e.target.closest('.month-year-btn') && !e.target.closest('.month-picker')) {
        setMonthPickerOpen(false);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [monthPickerOpen]);

  const matrix = useMemo(() => buildMonthMatrix(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const popRef = useRef();

  function openPopover(date, e, eventToEdit = null, dateIsoOverride = null) {
    setPopoverPos({ top: '50%', left: '50%' });
    const dateIso = dateIsoOverride || formatISO(date);
    setPopoverDate(dateIso);

    if (eventToEdit) {
      setEditingEvent(eventToEdit);
      setEditDateIso(dateIso);
      setForm({
        title: eventToEdit.title,
        start: eventToEdit.time?.startTime || '09:00',
        end: eventToEdit.time?.endTime || '10:00',
        notes: eventToEdit.notes || '',
        project: eventToEdit.projectId || '',
        color: eventToEdit.color || '#7fc59a',
        allDay: eventToEdit.time?.allDay || false,
        assignees: Array.isArray(eventToEdit.assignees)
          ? eventToEdit.assignees
              .map((a) => (typeof a === 'string' ? a : a.email))
              .filter(Boolean)
          : [],
      });
    } else {
      setEditingEvent(null);
      setEditDateIso(null);
      setForm({
        title: '',
        start: '09:00',
        end: '10:00',
        notes: '',
        project: '',
        color: '#7fc59a',
        allDay: false,
        assignees: [],
      });
    }

  }

  function closePopover() {
    setPopoverDate(null);
    setEditingEvent(null);
  }

  /* ---------- Save Event ---------- */
  async function saveEvent() {
    if (!form.title) return;
    const targetDate = editDateIso || popoverDate;
    if (!targetDate) return;
    if (!userId) {
      setError('You must be signed in to save events.');
      return;
    }

    const projectId = form.project || initialProjectFilter || '';
    const projectName =
      projects.find((p) => p.id === projectId)?.name ||
      projects.find((p) => p.id === projectId)?.projectNickname ||
      '';

    const evt = {
      id: editingEvent?.id || Date.now().toString(),
      title: form.title,
      time: { allDay: form.allDay, startTime: form.start, endTime: form.end },
      notes: form.notes,
      projectId,
      projectName,
      color: form.color,
      completed: editingEvent?.completed ?? false,
      createdAt: editingEvent?.createdAt || new Date().toISOString(),
      assignees: form.assignees || [],
    };

    setEventsMap((prev) => {
      const copy = { ...prev };
      const arr = [...(copy[targetDate] || [])];
      if (editingEvent) {
        const idx = arr.findIndex((e) => e.id === editingEvent.id);
        if (idx !== -1) arr[idx] = evt;
      } else arr.push(evt);
      copy[targetDate] = arr;
      return copy;
    });

    try {
      const res = await fetch(`${API_BASE}/api/planner/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, date: targetDate, event: evt }),
      });
      if (!res.ok) throw new Error(`Bad response ${res.status}`);
      triggerPlannerSync(); // 🔄 notify TodoList
    } catch (err) {
      console.error('Failed to save planner event', err);
      setError('Failed to save event.');
    }
    closePopover();
  }

  /* ---------- Delete Event ---------- */
  async function deleteEvent(dateIso, id) {
    setConfirmDelete(null);
    setEventsMap((prev) => {
      const copy = { ...prev };
      if (!copy[dateIso]) return prev;
      copy[dateIso] = copy[dateIso].filter((e) => e.id !== id);
      return copy;
    });
    if (expandedDay?.iso === dateIso) {
      const updatedEvents = (expandedDay.events || []).filter((e) => e.id !== id);
      setExpandedDay({ iso: dateIso, events: updatedEvents });
    }
    try {
      await fetch(`${API_BASE}/api/planner/events`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, date: dateIso, eventId: id }),
      });
      triggerPlannerSync(); // 🔄 notify TodoList
    } catch (err) {
      console.error('Failed to delete event', err);
    }
  }

  /* ---------- Toggle Completion ---------- */
  async function toggleEventComplete(dateIso, eventId) {
    const currentEvents = eventsMap[dateIso] || [];
    const target = currentEvents.find((e) => e.id === eventId);
    if (!target) return;

    const nextCompleted = !target.completed;
    const updatedEvents = currentEvents.map((e) =>
      e.id === eventId ? { ...e, completed: nextCompleted } : e
    );

    setEventsMap((prev) => ({ ...prev, [dateIso]: updatedEvents }));
    if (expandedDay?.iso === dateIso) {
      setExpandedDay({ iso: dateIso, events: updatedEvents });
    }

    try {
      const res = await fetch(
        `${API_BASE}/api/planner/events/${encodeURIComponent(eventId)}/complete`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, date: dateIso, completed: nextCompleted }),
        }
      );
      if (!res.ok) throw new Error(`Bad response ${res.status}`);
      triggerPlannerSync();
    } catch (err) {
      console.error('Failed to update completion', err);
      setEventsMap((prev) => ({ ...prev, [dateIso]: currentEvents }));
      if (expandedDay?.iso === dateIso) {
        setExpandedDay({ iso: dateIso, events: currentEvents });
      }
    }
  }

  /* ---------- Render ---------- */
  function renderEventBadges(dateIso) {
    const allEvents = eventsMap[dateIso] || [];
    if (!allEvents.length) return null;
    const visibleEvents = allEvents.slice(0, 2);
    const hiddenCount = allEvents.length - visibleEvents.length;
    return (
      <div className="cal-badges">
        {visibleEvents.map((e) => (
          <div
            key={e.id}
            className={`cal-badge ${e.completed ? 'done' : ''}`}
            style={{
              borderLeft: `4px solid ${e.color || projectColorMap[e.projectId] || '#7fc59a'}`,
            }}
            title={`${e.title}${!e.time?.allDay ? ` — ${formatAMPM(e.time?.startTime)}–${formatAMPM(e.time?.endTime)}` : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              setExpandedDay({ iso: dateIso, events: allEvents });
            }}
          >
            <span>{e.title.length > 18 ? e.title.slice(0, 18) + '…' : e.title}</span>
          </div>
        ))}
        {hiddenCount > 0 && (
          <button
            className="cal-more-btn"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedDay({ iso: dateIso, events: allEvents });
            }}
          >
            +{hiddenCount} more
          </button>
        )}
      </div>
    );
  }

  /* ---------- UI ---------- */
  return (
    <div className="calendar-page">
      {/* ---------- Navigation Header ---------- */}
      <div className="calendar-nav">
        <button className="nav-btn" onClick={() => setCursor(addMonths(cursor, -1))}>
          <ChevronLeft size={18} />
        </button>

        <button className="month-year-btn" onClick={() => setMonthPickerOpen((v) => !v)}>
          {cursor.toLocaleString('default', { month: 'long' })} {cursor.getFullYear()}
          <ChevronDown size={16} className={`chevron ${monthPickerOpen ? 'open' : ''}`} />
        </button>

        <button className="nav-btn" onClick={() => setCursor(addMonths(cursor, 1))}>
          <ChevronRight size={18} />
        </button>

        {monthPickerOpen && (
          <div className="month-picker">
            <div className="month-grid">
              {months.map((m, i) => (
                <button
                  key={m}
                  className={`month-option ${i === cursor.getMonth() ? 'active' : ''}`}
                  onClick={() => selectMonthYear(i, cursor.getFullYear())}
                >
                  {m.slice(0, 3)}
                </button>
              ))}
            </div>
            <div className="year-scroll">
              {years.map((y) => (
                <button
                  key={y}
                  className={`year-option ${y === cursor.getFullYear() ? 'active' : ''}`}
                  onClick={() => selectMonthYear(cursor.getMonth(), y)}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <table className="calendar-grid">
        <thead>
          <tr>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <th key={d}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((w, wi) => (
            <tr key={wi}>
              {w.map((day) => {
                const iso = formatISO(day);
                const isCurrent = day.getMonth() === cursor.getMonth();
                const isToday = formatISO(new Date()) === iso;
                return (
                  <td
                    key={iso}
                    className={`cal-cell ${isCurrent ? '' : 'muted'} ${isToday ? 'today' : ''}`}
                  >
                    <button className="cal-cell-btn" onClick={(e) => openPopover(day, e)}>
                      <span className="cal-date">{day.getDate()}</span>
                      {renderEventBadges(iso)}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* ---------- Add/Edit Popover ---------- */}
      {popoverDate && (
        <div className="popover-overlay" onClick={closePopover}>
          <div
            ref={popRef}
            className={`event-popover ${popoverDate ? 'open' : ''}`}
            style={{
              top: popoverPos.top,
              left: popoverPos.left,
              position: 'fixed',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popover-header">
              <input
                className="title-input"
                placeholder="Add title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <button className="close-btn" onClick={closePopover}>
                <X size={18} />
              </button>
            </div>

            <div className="date-row">
              {parseISO(popoverDate).toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}
            </div>

            <TimeRangePicker
              start={form.start}
              end={form.end}
              onChangeStart={(v) => setForm({ ...form, start: v })}
              onChangeEnd={(v) => setForm({ ...form, end: v })}
            />

            <label className="all-day">
              <input
                type="checkbox"
                checked={form.allDay}
                onChange={(e) => setForm({ ...form, allDay: e.target.checked })}
              />
              All day
            </label>

            {!hideProjectSelector ? (
              <select
                className="input project-select"
                value={form.project || initialProjectFilter || ''}
                onChange={(e) => setForm({ ...form, project: e.target.value })}
              >
                <option value="">No project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            ) : null}

            <div className={`assignees-field ${assigneeOpen ? 'active' : ''}`} ref={assigneeRef}>
              <div className="assignees-header">Assign to</div>
              <div className="assignee-input" onClick={() => setAssigneeOpen(true)}>
                {form.assignees.map((email) => {
                  const member = team.find((m) => m.email === email);
                  return (
                    <span key={email} className="assignee-pill">
                      {member?.name || email}
                      <button
                        type="button"
                        className="assignee-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          setForm((s) => ({
                            ...s,
                            assignees: s.assignees.filter((x) => x !== email),
                          }));
                        }}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
                <input
                  className="assignee-search"
                  placeholder={form.assignees.length === 0 ? 'Search people…' : ''}
                  value={assigneeSearch}
                  onChange={(e) => {
                    setAssigneeSearch(e.target.value);
                    setAssigneeOpen(true);
                  }}
                  onFocus={() => setAssigneeOpen(true)}
                />
              </div>
              {assigneeOpen && (
                <div className="assignee-list scrollable">
                  {teamList.length === 0 ? (
                    <div className="assignee-empty">No matches</div>
                  ) : (
                    teamList.map((member) => {
                      const key = member.email;
                      const checked = form.assignees.includes(key);
                      return (
                        <div
                          key={key}
                          className={`assignee-option ${checked ? 'checked' : ''}`}
                          onClick={() =>
                            setForm((s) => ({
                              ...s,
                              assignees: checked
                                ? s.assignees.filter((x) => x !== key)
                                : [...s.assignees, key],
                            }))
                          }
                        >
                          <span className="assignee-name">{member.name || member.email}</span>
                          <span className="assignee-email">{member.email}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* 🎨 Color Picker */}
            <div className="color-picker">
              <p className="color-picker-label">Event Color</p>
              <div className="color-options">
                {COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`color-swatch ${form.color === c ? 'active' : ''}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setForm((prev) => ({ ...prev, color: c }))}
                  />
                ))}
              </div>
            </div>

            <textarea
              className="input notes-input"
              placeholder="Add description or notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />

            <div className="popover-actions">
              {editingEvent && (
                <button
                  className="btn-muted"
                  onClick={() =>
                    setConfirmDelete({
                      id: editingEvent.id,
                      title: editingEvent.title,
                      dateIso: popoverDate,
                    })
                  }
                >
                  Delete
                </button>
              )}
              <button className="btn-save" onClick={saveEvent}>
                {editingEvent ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Expanded Drawer ---------- */}
      {expandedDay && (
        <div className="day-drawer-overlay" onClick={() => setExpandedDay(null)}>
          <aside className="day-drawer" onClick={(e) => e.stopPropagation()} role="dialog">
            <div className="day-drawer-header">
              <div>
                <h2>
                  {parseISO(expandedDay.iso).toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </h2>
                <p className="drawer-subtext">
                  {expandedDay.events.length} {expandedDay.events.length === 1 ? 'Event' : 'Events'}
                </p>
              </div>
              <button className="drawer-close-btn" onClick={() => setExpandedDay(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="day-drawer-list">
              {expandedDay.events.map((e) => (
                <div key={e.id} className="drawer-event-item">
                  <div
                    className="drawer-event-dot"
                    style={{
                      backgroundColor: e.color || projectColorMap[e.projectId] || '#7fc59a',
                    }}
                  ></div>

                  <div className="drawer-event-main">
                    <div className="drawer-event-header">
                      <h4 className={`drawer-event-title ${e.completed ? 'done' : ''}`}>
                        {e.title}
                      </h4>
                      <span
                        className="drawer-event-icon"
                        title={e.time?.allDay ? 'All day' : 'Timed'}
                      >
                        {e.time?.allDay ? '📅' : '⏰'}
                      </span>
                    </div>
                    <p className="drawer-event-time">
                      {e.time?.allDay
                        ? 'All day'
                        : `${formatAMPM(e.time?.startTime)} – ${formatAMPM(e.time?.endTime)}`}
                    </p>
                    {e.projectName && (
                      <p className="drawer-event-project">
                        Project: <strong>{e.projectName}</strong>
                      </p>
                    )}
                    {e.assignees?.length > 0 && (
                      <p className="drawer-event-assignees">
                        Assigned: <strong>{e.assignees.map((id) => assigneeName(id)).join(', ')}</strong>
                      </p>
                    )}
                    {e.notes && <p className="drawer-event-notes">{e.notes}</p>}
                  </div>

                  <div className="drawer-event-actions">
                    <label className="drawer-event-complete">
                      <input
                        type="checkbox"
                        checked={!!e.completed}
                        onChange={() => toggleEventComplete(expandedDay.iso, e.id)}
                      />
                      <span>Done</span>
                    </label>
                    <button
                      className="edit-btn"
                      onClick={() => {
                        setExpandedDay(null);
                        openPopover(parseISO(expandedDay.iso), null, e, expandedDay.iso);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() =>
                        setConfirmDelete({
                          id: e.id,
                          title: e.title,
                          dateIso: expandedDay.iso,
                        })
                      }
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}

      {/* ---------- Delete Confirmation ---------- */}
      {confirmDelete && (
        <div className="delete-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()} role="dialog">
            <h3>Delete Event</h3>
            <p>
              Are you sure you want to permanently delete <strong>{confirmDelete.title}</strong>?
            </p>
            <div className="delete-modal-actions">
              <button className="btn-muted" onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={() => deleteEvent(confirmDelete.dateIso, confirmDelete.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
